import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { RealtimeProvider } from "@/components/realtime/realtime-provider";
import { ErrorBoundary } from "@/components/error/error-boundary";
import { AuthProvider } from "@/contexts/AuthContext";
import { MandateProvider } from "@/contexts/mandate-context";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { CommandPalette } from "@/components/ui/command-palette";
import { useState } from "react";
import Index from "./pages/Index";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
// Profile page removed - integrated into Settings
import Operational from "./pages/Operational";
import FileMonitor from "./pages/FileMonitor";
import FileProcessor from "./pages/FileProcessor";
import Transactions from "./pages/Transactions";
import Validation from "./pages/Validation";
import ExportCenter from "./pages/ExportCenter";
import TeamManagement from "./pages/TeamManagement";
import ClientPortfolio from "./pages/ClientPortfolio";
import StrategicIntel from "./pages/StrategicIntel";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import ChartOfAccounts from "./pages/ChartOfAccounts";
import LearningRules from "./pages/LearningRules";
import Onboarding from "./pages/Onboarding";
import JournalEntries from "./pages/JournalEntries";
import VatReport from "./pages/VatReport";
import NotFound from "./pages/NotFound";
import Workspace from "./pages/Workspace";
import ReviewQueue from "./pages/ReviewQueue";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Impressum from "./pages/Impressum";
import DataSubjectRights from "./components/privacy/DataSubjectRights";
import ComplianceDashboard from "./components/privacy/ComplianceDashboard";
import PayPalTestPage from "./pages/PayPalTestPage";
import PayPalCallback from "./pages/PayPalCallback";
import { QueryError, MutationError } from "./types/errors";
import { JournalViewer } from "./components/booking/JournalViewer";

// Enhanced QueryClient configuration for Swiss Finance OS
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: QueryError, query) => {
      console.error(`Query error for key ${query.queryKey}:`, error);

      // Handle specific error types
      if (error?.status === 401) {
        // Handle authentication errors - could redirect to login
        console.warn('Authentication required - user may need to re-login');
      } else if (error?.status >= 500) {
        // Server errors - show user-friendly message
        console.error('Server error encountered - financial data may be temporarily unavailable');
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: MutationError, variables, context, mutation) => {
      console.error(`Mutation error for ${mutation.options.mutationKey}:`, error);

      // Handle critical financial operation errors
      if (error?.status >= 500) {
        console.error('Critical: Financial operation failed due to server error');
      }
    },
  }),
  defaultOptions: {
    queries: {
      // Cache configuration optimised for static identity data; fast-changing queries override locally
      staleTime: 10 * 60 * 1000,
      gcTime: 20 * 60 * 1000,

      // Error handling and retry logic
      retry: (failureCount, error: QueryError) => {
        // Don't retry on authentication errors
        if (error?.status === 401 || error?.status === 403) {
          return false;
        }
        // Don't retry on client errors (400-499)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 3 times for network errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Network mode for better offline handling
      networkMode: 'online',

      // Avoid redundant refetch cycles; realtime layer pushes critical changes
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: false,
    },
    mutations: {
      // Mutation retry for critical financial operations
      retry: (failureCount, error: MutationError) => {
        // Never retry mutations that would cause data corruption
        if (error?.status === 409 || error?.status === 422) {
          return false;
        }
        // Don't retry authentication errors
        if (error?.status === 401 || error?.status === 403) {
          return false;
        }
        // Retry network errors carefully for financial operations
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),

      // Network mode for mutations
      networkMode: 'online',
    },
  },
});

const App = () => {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <MandateProvider>
              <RealtimeProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <CommandPalette 
                    open={commandPaletteOpen} 
                    onOpenChange={setCommandPaletteOpen} 
                  />
                  <Routes>
                {/* Public routes */}
                <Route path="/" element={<Login />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                
                {/* Legal pages - publicly accessible */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/data-rights" element={<DataSubjectRights />} />

                {/* Protected routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Index />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/workspace"
                  element={
                    <ProtectedRoute>
                      <Workspace />
                    </ProtectedRoute>
                  }
                />
                {/* Profile route removed - now part of Settings */}
                <Route
                  path="/operational"
                  element={
                    <ProtectedRoute>
                      <Operational />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/operational/ai-intelligence"
                  element={
                    <ProtectedRoute>
                      <Operational />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/file-monitor"
                  element={
                    <ProtectedRoute>
                      <FileMonitor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/file-processor"
                  element={
                    <ProtectedRoute>
                      <FileProcessor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/transactions"
                  element={
                    <ProtectedRoute>
                      <Transactions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/review-queue"
                  element={
                    <ProtectedRoute>
                      <ReviewQueue />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/journal"
                  element={
                    <ProtectedRoute>
                      <JournalViewer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/validation"
                  element={
                    <ProtectedRoute>
                      <Validation />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/exports"
                  element={
                    <ProtectedRoute>
                      <ExportCenter />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/team-management"
                  element={
                    <ProtectedRoute requireRole="senior">
                      <TeamManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client-portfolio"
                  element={
                    <ProtectedRoute>
                      <ClientPortfolio />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/strategic-intel"
                  element={
                    <ProtectedRoute requireRole="partner">
                      <StrategicIntel />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      <Reports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/chart-of-accounts"
                  element={
                    <ProtectedRoute>
                      <ChartOfAccounts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/learning-rules"
                  element={
                    <ProtectedRoute>
                      <LearningRules />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/paypal-test"
                  element={
                    <ProtectedRoute>
                      <PayPalTestPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/paypal/callback"
                  element={
                    <ProtectedRoute>
                      <PayPalCallback />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/compliance"
                  element={
                    <ProtectedRoute requireRole="senior">
                      <ComplianceDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute>
                      <Onboarding />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/structured-onboarding"
                  element={
                    <ProtectedRoute>
                      <Onboarding />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/journal-entries"
                  element={
                    <ProtectedRoute>
                      <JournalEntries />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vat-report"
                  element={
                    <ProtectedRoute>
                      <VatReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/open-items"
                  element={
                    <ProtectedRoute>
                      <JournalEntries />
                    </ProtectedRoute>
                  }
                />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
                </BrowserRouter>
              </RealtimeProvider>
          </MandateProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
